import { useState } from 'react'
import type { ConversaDto } from '@crm/shared'
import { ErroRequisicao } from '../../api/cliente'
import { useEquipe } from '../../api/hooks'
import { useConexoes } from '../../api/hooks-chat'
import { useAgendarMensagem, useRespostasRapidas } from '../../api/hooks-operacao'
import { BotaoIcone } from '../../componentes/BotaoIcone'
import { useFecharAoClicarFora } from '../../componentes/fechar-fora'
import { IconeArquivo, IconeImagem, IconeMais, IconeRespostaRapida } from '../../componentes/icones'
import { Aviso, Botao, Campo, Modal } from '../../componentes/ui'
import { formatarDataHora } from '../../formato'
import { useModelosDaConversa } from '../../api/hooks-api-oficial'
import { SeletorSegmentado } from '../../componentes/pagina'
import {
  ESCOLHA_VAZIA,
  EscolhaDeModelo,
  pendenciaDoModelo,
  type EscolhaDoModelo,
} from './ModelosAprovados'
import { AvisoForaDoHorario } from '../../componentes/FimDoHorario'

/**
 * Respostas rápidas (5.1): acessadas por ícone dedicado, e também digitando
 * `/atalho` no campo de mensagem — que é como quem usa o dia inteiro prefere.
 */
export function SeletorRespostas({
  aoEscolher,
  filtro,
}: {
  aoEscolher: (conteudo: string) => void
  filtro?: string
}) {
  const { data: respostas } = useRespostasRapidas()
  const [aberto, setAberto] = useState(false)
  const caixa = useFecharAoClicarFora<HTMLDivElement>(aberto, () => setAberto(false))

  const termo = (filtro ?? '').toLowerCase()
  const lista = (respostas ?? []).filter(
    (r) =>
      !termo || r.atalho.toLowerCase().includes(termo) || r.conteudo.toLowerCase().includes(termo),
  )

  return (
    /*
      Sem `relative` de propósito: o painel se posiciona contra o rodapé, não
      contra este botão. Ancorado no botão ele saía pela lateral — com 384px
      de largura e o gatilho no meio da barra, o painel passava por cima da
      lista de conversas e ficava cortado. Preso ao rodapé, ele acompanha a
      largura do campo de mensagem e não tem por onde vazar.
    */
    <div ref={caixa}>
      <BotaoIcone
        rotulo="Resposta rápida"
        onClick={() => setAberto((a) => !a)}
        className={aberto ? 'bg-tinta-100 text-tinta-900' : undefined}
      >
        <IconeRespostaRapida className="h-5 w-5" />
      </BotaoIcone>

      {aberto ? (
        <div className="absolute bottom-full left-2 right-2 z-30 mb-1 rounded-xl ring-1 ring-tinta-200 bg-superficie shadow-media">
          <header className="border-b border-tinta-200 px-3 py-2 text-xs font-semibold uppercase text-tinta-400">
            Respostas rápidas
          </header>
          <ul className="max-h-72 overflow-y-auto">
            {lista.length === 0 ? (
              <li className="p-4 text-center text-sm text-tinta-400">
                Nenhuma cadastrada. Crie em Configuração → Respostas rápidas.
              </li>
            ) : (
              lista.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => {
                      aoEscolher(r.conteudo)
                      setAberto(false)
                    }}
                    className="w-full border-b border-tinta-100 px-3 py-2 text-left last:border-0 hover:bg-tinta-50"
                  >
                    <span className="flex items-center gap-2">
                      <code className="rounded bg-tinta-100 px-1 text-xs">/{r.atalho}</code>
                      {r.global ? (
                        <span className="text-[10px] uppercase text-tinta-400">global</span>
                      ) : null}
                    </span>
                    <span className="mt-0.5 line-clamp-2 block text-sm text-tinta-600">
                      {r.conteudo}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Agendar mensagem (5.1). Pop-up centralizado com vendedor de destino, data,
 * hora e conteúdo.
 *
 * Quando vai para vários contatos, o servidor espaça os disparos: o número já
 * foi bloqueado uma vez por mandar várias seguidas (regra 5).
 */
/**
 * O `+` do campo de mensagem: escolhe o tipo antes de abrir o explorador.
 *
 * Um botão único de anexo abria o explorador em "todos os arquivos", e achar
 * uma foto no meio de PDF e planilha é lento. Separando, cada opção já filtra
 * o que aparece: foto e vídeo numa, o resto na outra.
 *
 * Abre para cima porque o campo de mensagem mora no rodapé; para baixo, o
 * menu sairia da tela.
 */
export function SeletorAnexo({
  aoEscolher,
  desabilitado,
}: {
  /** `midia` filtra imagem e vídeo; `arquivo` aceita qualquer coisa. */
  aoEscolher: (tipo: 'midia' | 'arquivo') => void
  desabilitado?: boolean
}) {
  const [aberto, setAberto] = useState(false)
  const caixa = useFecharAoClicarFora<HTMLDivElement>(aberto, () => setAberto(false))

  const opcoes = [
    { tipo: 'midia' as const, rotulo: 'Fotos e vídeos', Icone: IconeImagem },
    { tipo: 'arquivo' as const, rotulo: 'Arquivo', Icone: IconeArquivo },
  ]

  return (
    <div ref={caixa} className="relative">
      <BotaoIcone
        rotulo="Anexar"
        onClick={() => setAberto((a) => !a)}
        disabled={desabilitado}
        className={aberto ? 'bg-tinta-100 text-tinta-900' : undefined}
      >
        <IconeMais className="h-5 w-5" />
      </BotaoIcone>

      {aberto ? (
        <ul className="absolute bottom-full left-0 z-30 mb-1 w-48 rounded-xl ring-1 ring-tinta-200 bg-superficie py-1 shadow-media">
          {opcoes.map((o) => (
            <li key={o.tipo}>
              <button
                type="button"
                onClick={() => {
                  aoEscolher(o.tipo)
                  setAberto(false)
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-tinta-600 hover:bg-tinta-50"
              >
                <o.Icone className="h-4 w-4 shrink-0" />
                {o.rotulo}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export function ModalAgendar({
  aberto,
  conversa,
  aoFechar,
}: {
  aberto: boolean
  conversa: ConversaDto
  aoFechar: () => void
}) {
  const agendar = useAgendarMensagem()
  const { data: usuarios } = useEquipe()
  const { data: conexoes } = useConexoes()

  const [conteudo, setConteudo] = useState('')
  const [quando, setQuando] = useState('')
  const [vendedorDestinoId, setVendedorDestinoId] = useState('')
  const [resultado, setResultado] = useState<string | null>(null)

  /*
    API oficial (ADR 0033): o agendamento pode levar um modelo aprovado. Texto
    livre continua possível, mas só sai se o cliente tiver escrito nas 24
    horas antes do envio; senão vira erro, com o motivo, na lista.
  */
  const oficial = conexoes?.find((c) => c.id === conversa.conexaoId)?.provedor === 'oficial'
  const [forma, setForma] = useState<'texto' | 'modelo'>('texto')
  const [escolha, setEscolha] = useState<EscolhaDoModelo>(ESCOLHA_VAZIA)
  const { data: modelos } = useModelosDaConversa(conversa.chamadoId, aberto && oficial)
  const comModelo = oficial && forma === 'modelo'
  const pendencia = comModelo ? pendenciaDoModelo(modelos, escolha) : null

  const erro = agendar.error instanceof ErroRequisicao ? agendar.error : null

  return (
    <Modal
      aberto={aberto}
      titulo={`Agendar mensagem para ${conversa.nome}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao onClick={aoFechar}>Fechar</Botao>
          <Botao
            variante="primario"
            carregando={agendar.isPending}
            disabled={!quando || (comModelo ? pendencia !== null : !conteudo.trim())}
            title={pendencia ?? undefined}
            onClick={() =>
              agendar.mutate(
                {
                  contatoIds: [conversa.contatoId],
                  conexaoId: conversa.conexaoId,
                  conteudo: comModelo ? '' : conteudo,
                  ...(comModelo && escolha.modeloId
                    ? { modelo: { modeloId: escolha.modeloId, valores: escolha.valores } }
                    : {}),
                  agendadoPara: new Date(quando),
                  vendedorDestinoId: vendedorDestinoId || null,
                },
                {
                  onSuccess: (r) => {
                    // A janela fica limpa: só a confirmação, para não parecer que falta agendar.
                    setConteudo('')
                    setQuando('')
                    setEscolha(ESCOLHA_VAZIA)
                    setResultado(
                      `Agendada para ${formatarDataHora(r.ultimoEnvioPrevisto)}. Dá para cancelar até o disparo.`,
                    )
                  },
                },
              )
            }
          >
            Agendar
          </Botao>
        </>
      }
    >
      <div className="space-y-3">
        {erro ? <Aviso tipo="erro">{erro.corpo.mensagem}</Aviso> : null}
        {resultado ? <Aviso tipo="sucesso">{resultado}</Aviso> : null}

        <Campo
          rotulo="Quando enviar"
          type="datetime-local"
          value={quando}
          onChange={(e) => setQuando(e.target.value)}
        />
        <AvisoForaDoHorario quando={quando} oQue="A mensagem sai" />

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-tinta-800">Passar o lead para</span>
          <select
            value={vendedorDestinoId}
            onChange={(e) => setVendedorDestinoId(e.target.value)}
            className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-3 py-2 text-sm"
          >
            <option value="">Manter o dono atual</option>
            {(usuarios ?? [])
              .filter((u) => u.ativo)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nome}
                </option>
              ))}
          </select>
          <span className="mt-1 block text-xs text-tinta-400">
            Ao disparar, o contato passa para este vendedor. A troca fica registrada no log e na
            conversa.
          </span>
        </label>

        {oficial ? (
          <SeletorSegmentado
            className="w-fit"
            rotulo="O que enviar"
            opcoes={[
              { valor: 'texto' as const, rotulo: 'Texto' },
              { valor: 'modelo' as const, rotulo: 'Modelo aprovado' },
            ]}
            valor={forma}
            aoEscolher={setForma}
          />
        ) : null}

        {comModelo ? (
          <EscolhaDeModelo modelos={modelos ?? []} escolha={escolha} aoMudar={setEscolha} />
        ) : (
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-tinta-800">Mensagem</span>
            <textarea
              value={conteudo}
              onChange={(e) => setConteudo(e.target.value)}
              rows={4}
              className="w-full rounded-md ring-1 ring-tinta-200 px-3 py-2 text-sm"
              placeholder="Bom dia! Passando para saber se ficou alguma dúvida sobre o orçamento."
            />
            {oficial ? (
              <span className="mt-1 block text-xs text-tinta-400">
                Pela API oficial, texto só sai se o cliente tiver escrito nas 24 horas antes do
                envio. Para retomar um cliente parado, use um modelo aprovado.
              </span>
            ) : null}
          </label>
        )}

        <Aviso>
          A mensagem sai mesmo que o cliente responda antes da data. Para cancelar, use a lista de
          agendamentos.
        </Aviso>
      </div>
    </Modal>
  )
}
